import { PRODUCT_STATUSES } from "@osmix/auth-policy"
import { z } from "zod"
import type { ZodType } from "zod"
import { CategoryComplianceSchema, ProductComplianceSchema, PublicComplianceDocumentSchema } from "./compliance"

export const ProductStatusSchema = z.enum(PRODUCT_STATUSES)
export type ProductStatus = z.infer<typeof ProductStatusSchema>

export const ProductVariantSchema = z
  .object({
    id: z.string(),
    color: z.string(),
    /** §6: hex цвета, резолвится на сервере из справочника названий (COLOR_HEX).
     *  Пусто, если название не распознано — клиент не обязан угадывать. */
    colorHex: z.string().optional(),
    size: z.string(),
    article: z.string().optional(),
    options: z.record(z.string(), z.string()).optional(),
    price: z.number(),
    oldPrice: z.number().optional(),
    stock: z.number().int().nonnegative(),
    photos: z.array(z.string()),
    position: z.number().int().nonnegative().optional(),
    weightGrams: z.number().int().positive().optional(),
    lengthCm: z.number().int().positive().optional(),
    widthCm: z.number().int().positive().optional(),
    heightCm: z.number().int().positive().optional(),
    /** Только в кабинете продавца: промодерированная базовая цена строки (миграция 112). */
    approvedPrice: z.number().optional(),
    /** Только в кабинете продавца: живая заявка на повышение цены строки. */
    pendingPrice: z
      .object({ price: z.number(), oldPrice: z.number().optional(), createdAt: z.string() })
      .optional(),
  })
  .refine((variant) => variant.oldPrice == null || variant.oldPrice > variant.price, {
    message: "oldPrice must be greater than price",
    path: ["oldPrice"],
  })
export type ProductVariant = z.infer<typeof ProductVariantSchema>

export const ProductBadgeSchema = z.enum([
  "handmade",
  "eco",
  "new",
  "limited",
  "customizable",
  "bestseller",
  /** «Распродажа» (fd-catalog-20, 31.10.2026): ставит продавец вручную. */
  "sale",
])
export type ProductBadge = z.infer<typeof ProductBadgeSchema>

/**
 * Бейджи, которые продавец ставит сам в форме товара (fd-catalog-20):
 * «Хит», «Новинка», «Распродажа», «Эко». Системные («Новинка» первые 90 дней,
 * «Кастомизация») шлюз добавляет сам при чтении.
 */
export const SELLER_PRODUCT_BADGES = ["bestseller", "new", "sale", "eco"] as const

/**
 * Дополнительные оси вариантов (fd-catalog-07, миграция 531): сверх цвета
 * (отдельный товар группы) и размера - материал и свой параметр продавца.
 * Значение оси у строки варианта - `variant.options[key]`.
 */
export const PRODUCT_VARIANT_AXES_MAX = 2
/** Ключи `options`, занятые под служебные значения строки, - не оси. */
export const PRODUCT_VARIANT_RESERVED_OPTION_KEYS = ["russianSize", "barcode", "size", "color"] as const
export const ProductVariantAxisSchema = z.object({
  /** `material` - материал; свой параметр - `param1`, `param2`. */
  key: z.string().regex(/^[a-z][a-zA-Z0-9]{0,29}$/),
  label: z.string().trim().min(1).max(30),
})
export type ProductVariantAxis = z.infer<typeof ProductVariantAxisSchema>

/**
 * Точки на фото галереи карточки (fd-catalog-03, миграция 531): продавец
 * ставит точку на фото и привязывает другой свой товар. Координаты - в
 * процентах кадра, как у меток идей (`@osmix/ui-primitives` pins).
 */
export const PRODUCT_HOTSPOTS_MAX_PER_PHOTO = 10
export const ProductHotspotPinSchema = z.object({
  id: z.string().min(1).max(60),
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  productId: z.string().min(1),
  /** Только в выдаче покупателю: мини-карточка привязанного товара. */
  product: z
    .object({
      id: z.string(),
      name: z.string(),
      brand: z.string(),
      price: z.number(),
      oldPrice: z.number().optional(),
      photo: z.string().optional(),
    })
    .optional(),
})
export type ProductHotspotPin = z.infer<typeof ProductHotspotPinSchema>
export const ProductPhotoHotspotsSchema = z.object({
  /** Адрес фото товара (или фото варианта), на котором стоят точки. */
  photo: z.string().min(1),
  pins: z.array(ProductHotspotPinSchema).max(PRODUCT_HOTSPOTS_MAX_PER_PHOTO),
})
export type ProductPhotoHotspots = z.infer<typeof ProductPhotoHotspotsSchema>

export const RichDescriptionBlockSchema = z.object({
  type: z.enum(["paragraph", "heading", "bullet"]),
  text: z.string().max(500),
})
export const RichDescriptionSchema = z.object({
  format: z.literal("osmix-rich-text"),
  blocks: z.array(RichDescriptionBlockSchema).max(40),
})
export type RichDescription = z.infer<typeof RichDescriptionSchema>

export const ProductAttributeValueSchema = z.union([z.string(), z.number(), z.boolean()])
export const ProductCustomAttributeSchema = z.object({
  label: z.string().min(1).max(80),
  value: z.string().min(1).max(200),
})
export type ProductCustomAttribute = z.infer<typeof ProductCustomAttributeSchema>

/**
 * Свои мерки продавца в размерной сетке товара (ответ Айталы 19.10.2026,
 * блок 15.10 п. 9): «Длина рукава», «Длина спинки» и т.п. К сетке категории
 * (обхваты груди, талии, бёдер) продавец добавляет столбцы, значения - по
 * размерам товара. Витрина показывает их в «Таблице размеров».
 */
export const PRODUCT_SIZE_MEASURES_MAX_COLUMNS = 6
export const ProductSizeMeasuresSchema = z.object({
  /** Названия мерок по порядку столбцов. */
  columns: z.array(z.string().trim().min(1).max(40)).max(PRODUCT_SIZE_MEASURES_MAX_COLUMNS),
  /** Значения по размеру варианта (ключ - размер как в варианте), по порядку столбцов; "" - не указано. */
  rows: z.record(z.array(z.string().max(20))),
})
export type ProductSizeMeasures = z.infer<typeof ProductSizeMeasuresSchema>

export const ProductCustomizationSchema = z.object({
  enabled: z.boolean(),
  options: z.array(z.object({ name: z.string(), minPrice: z.number(), photo: z.string().optional() })),
  /**
   * Тип товара «Кастомизация под предпочтения покупателя» (форма кабинета,
   * 01.10.2026): изделие меняют по пожеланиям покупателя, а не только по
   * списку вариантов. Необязательное — старые карточки и клиенты его не шлют.
   */
  byPreferences: z.boolean().optional(),
})
export type ProductCustomization = z.infer<typeof ProductCustomizationSchema>

export const ProductReturnPolicySchema = z.object({
  standard14Days: z.boolean(),
  customOrderException: z.boolean(),
  notes: z.string().max(500).optional(),
})
export type ProductReturnPolicy = z.infer<typeof ProductReturnPolicySchema>

export const ProductSellerInfoSchema = z.object({
  shopId: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  rating: z.number(),
  ratingCount: z.number().int().nonnegative(),
})
export type ProductSellerInfo = z.infer<typeof ProductSellerInfoSchema>

export const ProductModerationEventSchema = z.object({
  id: z.string(),
  productId: z.string(),
  moderatorId: z.string().optional(),
  actorId: z.string().optional(),
  oldStatus: ProductStatusSchema,
  newStatus: ProductStatusSchema,
  reason: z.string().optional(),
  createdAt: z.string(),
})
export type ProductModerationEvent = z.infer<typeof ProductModerationEventSchema>

export const CategoryAttributeDefinitionSchema = z.object({
  key: z.string().min(1).max(60).regex(/^[a-zA-Z0-9_-]+$/),
  label: z.string().min(1).max(120),
  type: z.enum(["text", "number", "boolean", "select"]),
  required: z.boolean().optional(),
  options: z.array(z.string().min(1).max(120)).optional(),
})
export type CategoryAttributeDefinition = z.infer<typeof CategoryAttributeDefinitionSchema>

export const CategoryAttributeSchema = z.object({
  categoryId: z.string(),
  attributes: z.array(CategoryAttributeDefinitionSchema),
  updatedAt: z.string().optional(),
  /** Размерная сетка категории (id из `@osmix/catalog-core/size-grids`) с учётом наследования. */
  sizeGrid: z.string().nullable().optional(),
  /** Путь категории от корня: «Женщинам», «Обувь», «Кроссовки и кеды». */
  path: z.array(z.string()).optional(),
  /** Конечный узел — только в него продавец кладёт новый товар. */
  isLeaf: z.boolean().optional(),
  /** Категория в архиве: новые товары в неё не кладутся. */
  archived: z.boolean().optional(),
  /** Нужен ли товару документ о соответствии (с наследованием, 17.10.2026). */
  compliance: CategoryComplianceSchema.optional(),
})
export type CategoryAttributeSchema = z.infer<typeof CategoryAttributeSchema>

/**
 * Заявка на повышение цены (миграция 112). Любое повышение промодерированной
 * базовой цены уходит на модерацию, а до решения действует прежняя цена.
 */
export const ProductPriceRequestStatusSchema = z.enum(["pending", "approved", "rejected", "cancelled"])
export type ProductPriceRequestStatus = z.infer<typeof ProductPriceRequestStatusSchema>

export const ProductPriceRequestSchema = z.object({
  id: z.string(),
  productId: z.string(),
  shopId: z.string(),
  /** Нет — цена товара без размеров. */
  variantId: z.string().optional(),
  variantLabel: z.string(),
  approvedPrice: z.number(),
  currentPrice: z.number(),
  currentOldPrice: z.number().optional(),
  requestedPrice: z.number(),
  requestedOldPrice: z.number().optional(),
  /** Рост базовой цены в процентах от промодерированной. */
  increasePct: z.number(),
  status: ProductPriceRequestStatusSchema,
  sellerReason: z.string().optional(),
  decisionReason: z.string().optional(),
  moderatorId: z.string().optional(),
  createdAt: z.string(),
  decidedAt: z.string().optional(),
  productName: z.string().optional(),
  productArticle: z.string().optional(),
  productPhoto: z.string().optional(),
  shopName: z.string().optional(),
})
export type ProductPriceRequest = z.infer<typeof ProductPriceRequestSchema>

/** Правила цен площадки для кабинета продавца. */
export const PricingRulesSchema = z.object({
  priceIncreaseRequiresModeration: z.literal(true),
  /** Скидка только на весь товар (одинаковая на все размеры). По умолчанию выключено. */
  discountWholeProductOnly: z.boolean(),
})
export type PricingRules = z.infer<typeof PricingRulesSchema>

/**
 * Товар группы вариантов (миграция 295, 16.10.2026): каждый вариант — отдельный
 * товар со своим артикулом, фото, размерами и модерацией; товары группы — цвета
 * одной вещи. Плитка переключателя цвета ведёт на соседний товар.
 */
export const ProductGroupVariantSchema = z.object({
  id: z.string(),
  /** Тот товар, у которого запрошена группа. */
  current: z.boolean(),
  name: z.string(),
  article: z.string(),
  color: z.string(),
  colorHex: z.string().optional(),
  photo: z.string().optional(),
  status: ProductStatusSchema,
  price: z.number(),
  oldPrice: z.number().optional(),
  stock: z.number(),
  /** Можно купить: остаток больше нуля или товар под заказ. */
  available: z.boolean(),
})
export type ProductGroupVariant = z.infer<typeof ProductGroupVariantSchema>

/** Canonical gateway product payload. Seller-only clients may receive shopId from auth context. */
export const ProductSchema = z.object({
  id: z.string(),
  shopId: z.string(),
  name: z.string(),
  article: z.string(),
  brand: z.string(),
  /** FK into the managed category tree. */
  categoryId: z.string(),
  /** Display name resolved from the category tree at read time. */
  category: z.string(),
  color: z.string(),
  sizes: z.array(z.string()),
  status: ProductStatusSchema,
  /** Группа товаров-вариантов; нет — товар без группы (аддитивное, 16.10.2026). */
  variantGroupId: z.string().optional(),
  /**
   * Товары группы по порядку, включая этот (`current`). Покупателю — только
   * видимые на витрине (в карточке товара), продавцу — все, кроме корзины.
   */
  groupVariants: z.array(ProductGroupVariantSchema).optional(),
  sellerInfo: ProductSellerInfoSchema.optional(),
  latestRejectionReason: z.string().optional(),
  blockReason: z.string().optional(),
  moderationHistory: z.array(ProductModerationEventSchema).optional(),
  /** Только в кабинете продавца: промодерированная базовая цена и живые заявки на повышение. */
  approvedPrice: z.number().optional(),
  pendingPriceChanges: z.array(ProductPriceRequestSchema).optional(),
  /** Ответ на сохранение: сколько повышений цены отправлено на модерацию. */
  priceChangesSubmitted: z.number().int().nonnegative().optional(),
  price: z.number(),
  oldPrice: z.number().optional(),
  /** fd-promo-03: действующая акция магазина; `price` уже со скидкой акции. */
  activePromo: z
    .object({
      id: z.string(),
      title: z.string(),
      discountType: z.enum(["percent", "amount"]),
      value: z.number(),
      endsAt: z.string(),
      price: z.number(),
    })
    .optional(),
  stock: z.number(),
  photos: z.array(z.string()),
  variants: z.array(ProductVariantSchema).optional(),
  description: z.string(),
  descriptionRich: RichDescriptionSchema.optional(),
  videoUrl: z.string().optional(),
  /**
   * Место видео среди медиа карточки (0 — первым, перед фото). Нет — видео
   * после всех фото, как было раньше. Аддитивное (01.10.2026).
   */
  videoPosition: z.number().int().nonnegative().optional(),
  categoryAttributes: z.record(ProductAttributeValueSchema).optional(),
  customAttributes: z.array(ProductCustomAttributeSchema).optional(),
  /** Свои мерки продавца в таблице размеров (миграция 411); нет - только сетка категории. */
  sizeMeasures: ProductSizeMeasuresSchema.optional(),
  /** Минимальный заказ, шт. (миграция 422): витрина - бейдж «от N шт.», корзина - не меньше N. Нет - без ограничения. */
  minOrderQty: z.number().int().min(2).optional(),
  /** Оси вариантов сверх цвета и размера (миграция 531); нет - только цвет и размер. */
  variantAxes: z.array(ProductVariantAxisSchema).max(PRODUCT_VARIANT_AXES_MAX).optional(),
  /** Точки на фото галереи (миграция 531); нет - точек нет. */
  photoHotspots: z.array(ProductPhotoHotspotsSchema).optional(),
  customization: ProductCustomizationSchema.optional(),
  returnPolicy: ProductReturnPolicySchema.optional(),
  tags: z.array(z.string()).optional(),
  badges: z.array(ProductBadgeSchema).optional(),
  /** Бейджи, поставленные продавцом (без системных «Новинка 90 дней» и «Кастомизация»). */
  manualBadges: z.array(ProductBadgeSchema).optional(),
  keywords: z.array(z.string()),
  /** Ключевые фразы продавца (миграция 116). */
  keyPhrases: z.array(z.string()).optional(),
  /** Срок гарантии в днях; нет — не указан (миграция 116). */
  warrantyDays: z.number().int().nonnegative().optional(),
  noWarranty: z.boolean().optional(),
  /** Признак 18+ (миграция 116). */
  adultOnly: z.boolean().optional(),
  weightGrams: z.number().optional(),
  lengthCm: z.number().optional(),
  widthCm: z.number().optional(),
  heightCm: z.number().optional(),
  // ── §6 Поля витрины товара для мобильного макета (аддитивны, best-effort) ──
  /** Изображение товара «в вырезе» (PNG на прозрачном фоне) для коллажных блоков.
   *  Пусто, если у товара его нет (в текущих данных отсутствует). */
  cutoutImageUrl: z.string().optional(),
  /** Всего опубликованных товаров у магазина (для «Все товары Store»). */
  storeProductCount: z.number().int().nonnegative().optional(),
  /** Ярлык категории для блока «Все куртки Store» (мн. ч.). */
  storeCategoryLabel: z.string().optional(),
  /** Товаров у магазина в этой категории (для «123 товара»). */
  storeCategoryProductCount: z.number().int().nonnegative().optional(),
  /** Кабинет и консоль: документ о соответствии и состояние товара (17.10.2026). */
  compliance: ProductComplianceSchema.optional(),
  /** Витрина: действующий документ о соответствии, без файла. */
  complianceDocument: PublicComplianceDocumentSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type Product = z.infer<typeof ProductSchema>

/** Transitional seller-console product payload used before every client receives shopId/categoryId. */
export const SellerProductSchema = ProductSchema.extend({
  shopId: z.string().optional(),
  categoryId: z.string().optional(),
})
export type SellerProduct = z.infer<typeof SellerProductSchema>

/** Раздел витрины (миграция 087). */
export const CatalogSectionSchema = z.enum(["clothing", "shoes", "accessories", "beauty", "home", "kids", "other"])
export type CatalogSection = z.infer<typeof CatalogSectionSchema>
/** Пол (миграция 087): `kids` — детское, где пол не спрашивают. */
export const CatalogGenderSchema = z.enum(["female", "male", "unisex", "kids"])
export type CatalogGender = z.infer<typeof CatalogGenderSchema>

/**
 * Узел управляемого дерева категорий (до трёх уровней: аудитория → тип → вид).
 * Одно дерево на витрину, приложение, кабинет и консоль — клиенты не держат
 * своих списков категорий. Раздел, пол и сетка приходят уже с наследованием.
 */
export type Category = {
  id: string
  name: string
  children?: Category[]
  section?: CatalogSection
  gender?: CatalogGender
  /** Размерная сетка (id из `@osmix/catalog-core/size-grids`). */
  sizeGrid?: string
  /** Картинка категории; нет — клиент показывает свою заглушку. */
  imageUrl?: string
}
export const CategorySchema: ZodType<Category> = z.lazy(() =>
  z.object({
    id: z.string(),
    name: z.string(),
    children: z.array(CategorySchema).optional(),
    section: CatalogSectionSchema.optional(),
    gender: CatalogGenderSchema.optional(),
    sizeGrid: z.string().optional(),
    imageUrl: z.string().optional(),
  }),
)

/** Flat admin-maintenance category row. */
export const CategoryRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
  /** Раздел и пол, заданные на самой категории (без наследования). */
  section: CatalogSectionSchema.nullable().optional(),
  gender: CatalogGenderSchema.nullable().optional(),
  /** Своя размерная сетка (без наследования). */
  sizeGrid: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  archived: z.boolean().optional(),
  productCount: z.number().int().optional(),
  publishedCount: z.number().int().optional(),
  /** Только у архивного узла: куда вести старые ссылки (см. `ArchivedCategory`). */
  successorId: z.string().nullable().optional(),
  successors: z
    .object({ female: z.string().optional(), male: z.string().optional(), kids: z.string().optional() })
    .nullable()
    .optional(),
})
export type CategoryRow = z.infer<typeof CategoryRowSchema>

/** Аудитория дерева v2: корни «Женщинам», «Мужчинам», «Детям». */
export type CategoryAudience = "female" | "male" | "kids"

/**
 * Архивный узел дерева (`GET /catalog/categories/archived`): его нет в дереве
 * клиентов, но на него ведут старые ссылки, сохранённые ленты и подборки.
 * Преемник — по той же таблице, по которой миграция 134 переносила товары
 * (`ARCHIVED_V1_SUCCESSORS` в catalog-core), и только живой узел.
 */
export type ArchivedCategory = {
  id: string
  name: string
  /** Преемник по умолчанию («Женщинам», если есть); `null` — преемника нет, ссылка ведёт в корень каталога. */
  successorId: string | null
  /** Преемник для каждой аудитории («Обувь» → обувь Женщинам / Мужчинам / Детям). */
  successors: Partial<Record<CategoryAudience, string>> | null
}
export const ArchivedCategorySchema: ZodType<ArchivedCategory> = z.object({
  id: z.string(),
  name: z.string(),
  successorId: z.string().nullable(),
  successors: z
    .object({ female: z.string().optional(), male: z.string().optional(), kids: z.string().optional() })
    .nullable(),
})

export const CategoryInputSchema = z.object({
  name: z.string(),
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
  sizeGrid: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  archived: z.boolean().optional(),
})
export type CategoryInput = z.infer<typeof CategoryInputSchema>

export const ProductListTabSchema = z.enum([
  "all",
  "unpublished",
  "improvable",
  "drafts",
  "blocked",
  "trash",
])
export type ProductListTab = z.infer<typeof ProductListTabSchema>

export const ProductListFilterSchema = z.object({
  tab: ProductListTabSchema,
  search: z.string().optional(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
})
export type ProductListFilter = z.infer<typeof ProductListFilterSchema>

export const ProductSortSchema = z.enum(["relevance", "price-asc", "price-desc", "newest"])
export type ProductSort = z.infer<typeof ProductSortSchema>

export const BuyerProductQuerySchema = z.object({
  search: z.string().optional(),
  shopId: z.string().optional(),
  categoryId: z.string().optional(),
  brands: z.array(z.string()).optional(),
  colors: z.array(z.string()).optional(),
  sizes: z.array(z.string()).optional(),
  priceMin: z.number().optional(),
  priceMax: z.number().optional(),
  inStock: z.boolean().optional(),
  /** Город доставки покупателя (регионализация). Принимается и прокидывается в
   *  поиск; сегодня не сужает выдачу — фильтр включится, когда появятся
   *  per-city данные покрытия курьерской доставки (delivery_zones). */
  city: z.string().optional(),
  sort: ProductSortSchema.optional(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().default(24),
})
export type BuyerProductQuery = z.infer<typeof BuyerProductQuerySchema>

export const FacetValueSchema = z.object({
  value: z.string(),
  label: z.string().optional(),
  count: z.number().int().nonnegative(),
})
export type FacetValue = z.infer<typeof FacetValueSchema>

export type CategoryFacet = {
  value: string
  label: string
  count: number
  children?: CategoryFacet[]
}
export const CategoryFacetSchema: ZodType<CategoryFacet> = z.lazy(() =>
  z.object({
    value: z.string(),
    label: z.string(),
    count: z.number().int().nonnegative(),
    children: z.array(CategoryFacetSchema).optional(),
  }),
)

/**
 * Размеры одной шкалы. Смешивать шкалы в одном ряду нельзя: в обувной выдаче не
 * должно быть S / M / L, а в одежде — 39-го размера. `scale` — техническое имя
 * группы, `label` — подпись для фильтра.
 */
export const SizeFacetGroupSchema = z.object({
  scale: z.string(),
  label: z.string(),
  values: z.array(FacetValueSchema),
})
export type SizeFacetGroup = z.infer<typeof SizeFacetGroupSchema>

export const ProductFacetsSchema = z.object({
  categories: z.array(CategoryFacetSchema),
  brands: z.array(FacetValueSchema),
  colors: z.array(FacetValueSchema),
  /** Плоский список (нормализованный и отсортированный) — для старых клиентов. */
  sizes: z.array(FacetValueSchema),
  /** Тот же набор, разложенный по размерным шкалам. */
  sizeGroups: z.array(SizeFacetGroupSchema).default([]),
  price: z.object({ min: z.number(), max: z.number() }),
})
export type ProductFacets = z.infer<typeof ProductFacetsSchema>

export const PageMetaSchema = z.object({
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
})
export type PageMeta = z.infer<typeof PageMetaSchema>

export const ProductPageSchema = z.object({
  data: z.array(ProductSchema),
  meta: PageMetaSchema,
})
export type ProductPage = z.infer<typeof ProductPageSchema>

export const CreateProductRequestSchema = z.object({
  name: z.string(),
  brand: z.string(),
  categoryId: z.string(),
  sellerArticle: z.string(),
  color: z.string(),
  description: z.string().max(2000),
  descriptionRich: RichDescriptionSchema.optional(),
  videoUrl: z.string().optional(),
  /** См. `ProductSchema.videoPosition`; `null` — видео после фото. */
  videoPosition: z.number().int().nonnegative().nullable().optional(),
  categoryAttributes: z.record(ProductAttributeValueSchema).optional(),
  customAttributes: z.array(ProductCustomAttributeSchema).max(10).optional(),
  /** Свои мерки продавца; `null` - убрать все. */
  sizeMeasures: ProductSizeMeasuresSchema.nullable().optional(),
  /** Минимальный заказ, шт.; `null` или 1 - без ограничения. */
  minOrderQty: z.number().int().min(1).max(100000).nullable().optional(),
  /** Оси вариантов (материал, свой параметр); не передано - не менять, [] - убрать. */
  variantAxes: z.array(ProductVariantAxisSchema).max(PRODUCT_VARIANT_AXES_MAX).optional(),
  /** Точки на фото; не передано - не менять, [] - убрать все. */
  photoHotspots: z.array(ProductPhotoHotspotsSchema).max(20).optional(),
  returnPolicy: ProductReturnPolicySchema.optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).optional(),
  badges: z.array(ProductBadgeSchema).max(6).optional(),
  keywords: z.array(z.string()),
  keyPhrases: z.array(z.string()),
  /** fd-catalog-29: не больше 10 фото у карточки (раньше через API проходило любое число). */
  photos: z.array(z.string()).max(10),
  shelfLife: z.number().optional(),
  noWarranty: z.boolean(),
  madeToOrder: z.boolean(),
  madeToOrderPrice: z.number().optional(),
  /** Срок изготовления под заказ, дней (пишется в returnPolicy.madeToOrderLeadTimeDays). */
  madeToOrderLeadTimeDays: z.number().int().nonnegative().optional(),
  /** Признак 18+. */
  adultOnly: z.boolean().optional(),
  /**
   * Куда сохранить новую карточку: черновик (виден только продавцу, без
   * проверки полноты) или сразу на модерацию. По умолчанию — на модерацию, как
   * было до появления черновиков. При правке игнорируется: статус меняет
   * `/status`.
   */
  status: z.enum(["draft", "moderation"]).optional(),
  characteristics: z.record(z.string()),
  customization: z.object({
    enabled: z.boolean(),
    options: z.array(z.object({ name: z.string(), minPrice: z.number(), photo: z.string().optional() })),
    /** См. `ProductCustomizationSchema.byPreferences`. */
    byPreferences: z.boolean().optional(),
  }),
  delivery: z.object({
    length: z.number(),
    width: z.number(),
    height: z.number(),
    weight: z.number(),
    /**
     * Флаги способов доставки — устаревшие: шлюз их не хранит (пишутся только
     * габариты), витрина, мобилка и консоль не читают, способы доставки
     * настраиваются для всего магазина. Форма кабинета с 01.10.2026 их не шлёт;
     * поля оставлены необязательными для совместимости со старыми клиентами.
     */
    ownDelivery: z.boolean().optional(),
    expressDelivery: z.boolean().optional(),
    pickup: z.boolean().optional(),
  }),
  variants: z.array(ProductVariantSchema),
  /**
   * Объяснение резкого роста цены. Площадка пропускает обычные правки сразу, а
   * рост выше порога уводит на проверку — и тогда модератору нужна причина.
   * Снижение цены (скидка) объяснений не требует.
   */
  priceChangeReason: z.string().max(500).optional(),
  /**
   * Документ о соответствии из библиотеки продавца (17.10.2026); null -
   * отвязать. Не передан - не менять. Необязательное для старых клиентов.
   */
  complianceDocumentId: z.string().nullable().optional(),
  /**
   * «Персонализация серийного товара» (31.10.2026): гравировка, печать,
   * вышивка на готовом изделии. Снимает исключение «под заказ» - документ
   * нужен на основу. Не передан - не менять.
   */
  complianceSerialBase: z.boolean().optional(),
})
export type CreateProductRequest = z.infer<typeof CreateProductRequestSchema>

export const UpdateProductRequestSchema = CreateProductRequestSchema.partial()
export type UpdateProductRequest = z.infer<typeof UpdateProductRequestSchema>

export const ProductImportFormatSchema = z.literal("osmix-product-import")
export const ProductImportVersionSchema = z.literal(1)

export const ProductImportRowSchema = CreateProductRequestSchema.extend({
  /** Present only when updating an existing product owned by the authenticated seller. */
  id: z.string().optional(),
})
export type ProductImportRow = z.infer<typeof ProductImportRowSchema>

export const ProductImportDocumentSchema = z.object({
  format: ProductImportFormatSchema,
  version: ProductImportVersionSchema,
  exportedAt: z.string().optional(),
  products: z.array(ProductImportRowSchema).min(1).max(200),
})
export type ProductImportDocument = z.infer<typeof ProductImportDocumentSchema>

export const ProductImportErrorSchema = z.object({
  index: z.number().int().nonnegative(),
  field: z.string().optional(),
  message: z.string(),
})
export type ProductImportError = z.infer<typeof ProductImportErrorSchema>

export const ProductImportResultSchema = z.object({
  format: ProductImportFormatSchema,
  version: ProductImportVersionSchema,
  valid: z.boolean(),
  total: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  errors: z.array(ProductImportErrorSchema),
  products: z.array(ProductSchema).optional(),
})
export type ProductImportResult = z.infer<typeof ProductImportResultSchema>

/**
 * Правила автомодерации правок карточек. Обычная правка публикуется сразу;
 * ЛЮБОЕ повышение промодерированной цены уходит заявкой на модерацию (порога
 * больше нет, встреча 25.09.2026), порча карточки — зашита в правило.
 */
export const ModerationSettingsSchema = z.object({
  /**
   * Устарело: порог роста цены без проверки. С 25.09.2026 не действует —
   * любое повышение идёт на модерацию. Поле оставлено, чтобы старые клиенты и
   * сохранённые настройки не ломали разбор.
   */
  priceIncreasePct: z.number().min(0).max(1000).optional(),
  /**
   * Скидка только на весь товар: одинаковая на все размеры или никакой.
   * Не задано — берётся из DISCOUNT_WHOLE_PRODUCT_ONLY, по умолчанию выключено.
   * Нужна ли скидка на отдельный размер — решение заказчика.
   */
  discountWholeProductOnly: z.boolean().optional(),
})
export type ModerationSettings = z.infer<typeof ModerationSettingsSchema>

export const DEFAULT_MODERATION_SETTINGS: ModerationSettings = { discountWholeProductOnly: false }
